import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Activity, RefreshCw, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

interface ServiceStatus {
  name: string;
  status: "operational" | "degraded" | "outage";
  latency?: number;
}

const statusConfig = {
  operational: { icon: CheckCircle2, label: "Operacional", color: "text-green-400", bg: "bg-green-500/10", dot: "bg-green-500" },
  degraded: { icon: AlertTriangle, label: "Degradado", color: "text-yellow-400", bg: "bg-yellow-500/10", dot: "bg-yellow-500" },
  outage: { icon: XCircle, label: "Fora do ar", color: "text-red-400", bg: "bg-red-500/10", dot: "bg-red-500" },
};

const Status = () => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState<Date>(new Date());
  const [checking, setChecking] = useState(false);

  const checkServices = useCallback(async () => {
    setChecking(true);
    const results: ServiceStatus[] = [];

    // Check Database (via a lightweight query)
    try {
      const start = performance.now();
      const { error } = await supabase.from("plans").select("id").limit(1);
      const latency = Math.round(performance.now() - start);
      results.push({
        name: "Banco de Dados",
        status: error ? "outage" : latency > 3000 ? "degraded" : "operational",
        latency,
      });
    } catch {
      results.push({ name: "Banco de Dados", status: "outage" });
    }

    // Check Auth service
    try {
      const start = performance.now();
      const { error } = await supabase.auth.getSession();
      const latency = Math.round(performance.now() - start);
      results.push({
        name: "Autenticação",
        status: error ? "outage" : latency > 3000 ? "degraded" : "operational",
        latency,
      });
    } catch {
      results.push({ name: "Autenticação", status: "outage" });
    }

    // Check Storage
    try {
      const start = performance.now();
      const { error } = await supabase.storage.from("product-images").list("", { limit: 1 });
      const latency = Math.round(performance.now() - start);
      results.push({
        name: "Armazenamento de Arquivos",
        status: error ? "degraded" : latency > 3000 ? "degraded" : "operational",
        latency,
      });
    } catch {
      results.push({ name: "Armazenamento de Arquivos", status: "degraded" });
    }

    // Check Edge Functions (via a lightweight call)
    try {
      const start = performance.now();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/get-order-tracking`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
        body: JSON.stringify({ order_id: "health-check" }),
      });
      const latency = Math.round(performance.now() - start);
      // 400 is expected (invalid order_id), 5xx would be an issue
      results.push({
        name: "Backend Functions",
        status: res.status >= 500 ? "outage" : latency > 5000 ? "degraded" : "operational",
        latency,
      });
    } catch {
      results.push({ name: "Backend Functions", status: "outage" });
    }

    // Check Realtime
    try {
      const start = performance.now();
      const channelName = `health-check-${Date.now()}`;
      const channel = supabase.channel(channelName);
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          supabase.removeChannel(channel);
          reject(new Error("timeout"));
        }, 8000);
        channel.subscribe((status) => {
          if (status === "SUBSCRIBED") {
            clearTimeout(timeout);
            supabase.removeChannel(channel);
            resolve();
          }
          // Ignore intermediate statuses like CHANNEL_ERROR, just wait for SUBSCRIBED or timeout
        });
      });
      const latency = Math.round(performance.now() - start);
      results.push({ name: "Tempo Real (Realtime)", status: latency > 5000 ? "degraded" : "operational", latency });
    } catch {
      results.push({ name: "Tempo Real (Realtime)", status: "degraded" });
    }

    // Static services that depend on the above
    const dbOk = results.find(r => r.name === "Banco de Dados")?.status === "operational";
    results.push(
      { name: "Cardápio Digital", status: dbOk ? "operational" : "degraded" },
      { name: "Sistema de Pedidos", status: dbOk ? "operational" : "degraded" },
      { name: "Painel da Cozinha", status: dbOk ? "operational" : "degraded" },
    );

    setServices(results);
    setLastChecked(new Date());
    setLoading(false);
    setChecking(false);
  }, []);

  useEffect(() => {
    checkServices();
    const interval = setInterval(checkServices, 60000); // Auto-check every 60s
    return () => clearInterval(interval);
  }, [checkServices]);

  const allOperational = services.length > 0 && services.every((s) => s.status === "operational");
  const hasOutage = services.some((s) => s.status === "outage");

  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-24">
        <div className="container max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" /> Voltar ao início
          </Link>

          <motion.div className="mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Activity className="h-8 w-8 text-primary" />
                <h1 className="text-4xl md:text-5xl font-bold">Status do Sistema</h1>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={checkServices}
                disabled={checking}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
                Verificar
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                allOperational ? "bg-green-500/10 text-green-400" :
                hasOutage ? "bg-red-500/10 text-red-400" :
                "bg-yellow-500/10 text-yellow-400"
              }`}>
                {allOperational ? <CheckCircle2 className="h-4 w-4" /> :
                 hasOutage ? <XCircle className="h-4 w-4" /> :
                 <AlertTriangle className="h-4 w-4" />}
                {allOperational ? "Todos os sistemas operacionais" :
                 hasOutage ? "Alguns sistemas fora do ar" :
                 "Alguns sistemas com degradação"}
              </div>
              <span className="text-xs text-muted-foreground">
                Última verificação: {lastChecked.toLocaleTimeString("pt-BR")}
              </span>
            </div>

            {/* Live pulse indicator */}
            <div className="flex items-center gap-2 mt-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              <span className="text-xs text-muted-foreground">Monitoramento em tempo real — atualiza a cada 60s</span>
            </div>
          </motion.div>

          {/* Services */}
          <div className="mb-12">
            <h2 className="text-xl font-bold mb-4">Serviços</h2>
            {loading ? (
              <div className="flex items-center justify-center py-12 gap-3 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Verificando serviços...</span>
              </div>
            ) : (
              <div className="space-y-2">
                {services.map((s) => {
                  const cfg = statusConfig[s.status];
                  return (
                    <motion.div
                      key={s.name}
                      className="flex items-center justify-between p-4 rounded-xl bg-gradient-card border border-border"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                        <span className="font-medium text-sm">{s.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {s.latency != null && (
                          <span className="text-xs text-muted-foreground">{s.latency}ms</span>
                        )}
                        <span className={`flex items-center gap-1.5 text-xs font-medium ${cfg.color}`}>
                          <cfg.icon className="h-4 w-4" />
                          {cfg.label}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="p-6 rounded-xl bg-gradient-card border border-border">
            <h2 className="text-lg font-bold mb-2">Sobre o monitoramento</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Esta página verifica em tempo real a disponibilidade dos principais serviços da plataforma Via Menu.
              Os testes são executados diretamente do seu navegador a cada 60 segundos, medindo latência e
              disponibilidade de banco de dados, autenticação, armazenamento, funções backend e comunicação em tempo real.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Status;
